import { useQuery } from '@tanstack/react-query';
import { destinationPagePublicPath } from '@utazzo/shared';
import { UzButton, UzCollapsibleSection, UzInput } from '@utazzo/ui';
import { Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { listBlogPosts } from '../blog/blog.api';
import { listPages } from '../pages/pages.api';

import { newTempId, type FlatDraftItem, type NavItemType } from './navigation-tree.utils';

import { list as listCategoryPages } from '@/features/category-pages/category-pages.api';
import { list as listDestinationPages } from '@/features/destination-pages/destination-pages.api';
import { useDestinationsList } from '@/features/destinations/use-destinations';

export interface MenuItemDraftInput {
  label: string;
  href: string | null;
  pageId: string | null;
  itemType: NavItemType;
}

interface Props {
  canWrite: boolean;
  onAdd: (items: MenuItemDraftInput[]) => void;
  footerMode?: boolean;
}

type SourceTab = 'recent' | 'all' | 'search';

const SourceTabs = ({
  tab,
  onTab,
}: {
  tab: SourceTab;
  onTab: (t: SourceTab) => void;
}): React.JSX.Element => (
  <div className="mb-2 flex gap-1 border-b border-(--border) pb-2">
    {(
      [
        ['recent', 'Most Recent'],
        ['all', 'View All'],
        ['search', 'Search'],
      ] as const
    ).map(([id, label]) => (
      <button
        key={id}
        type="button"
        onClick={() => onTab(id)}
        className={`rounded px-2 py-1 text-xs font-medium ${
          tab === id
            ? 'bg-(--accent) text-(--primary)'
            : 'text-(--fg-faint) hover:bg-(--z-50) hover:text-(--fg)'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const CheckList = ({
  rows,
  selected,
  onToggle,
  onToggleAll,
  disabled,
}: {
  rows: { id: string; label: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  disabled: boolean;
}): React.JSX.Element => (
  <div className="rounded border border-(--border) bg-(--z-50) p-2">
    {rows.length > 0 && (
      <div className="mb-2 flex items-center border-b border-(--border) pb-2">
        <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-(--fg-faint)">
          <input
            type="checkbox"
            checked={rows.every((r) => selected.has(r.id))}
            disabled={disabled}
            onChange={onToggleAll}
          />
          Select All
        </label>
      </div>
    )}
    <div className="max-h-52 overflow-y-auto">
      {rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-(--fg-faint)">No items found.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((row) => (
            <li key={row.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-(--bg)">
                <input
                  type="checkbox"
                  checked={selected.has(row.id)}
                  disabled={disabled}
                  onChange={() => onToggle(row.id)}
                />
                <span className="truncate">{row.label}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

type DestinationScope = 'all' | 'domestic' | 'international';

const DestinationScopeTabs = ({
  scope,
  onScope,
}: {
  scope: DestinationScope;
  onScope: (s: DestinationScope) => void;
}): React.JSX.Element => (
  <div className="mb-2 flex flex-wrap gap-1 border-b border-(--border) pb-2">
    {(
      [
        ['all', 'All'],
        ['domestic', 'Domestic'],
        ['international', 'International'],
      ] as const
    ).map(([id, label]) => (
      <button
        key={id}
        type="button"
        onClick={() => onScope(id)}
        className={`rounded px-2 py-1 text-xs font-medium ${
          scope === id
            ? 'bg-(--accent) text-(--primary)'
            : 'text-(--fg-faint) hover:bg-(--z-50) hover:text-(--fg)'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

/** WordPress-style left column: pick Pages, Posts, Destinations, Categories, or Custom Links. */
export const AddMenuItemsPanel = ({
  canWrite,
  onAdd,
  footerMode = false,
}: Props): React.JSX.Element => {
  const [pagesTab, setPagesTab] = useState<SourceTab>('recent');
  const [postsTab, setPostsTab] = useState<SourceTab>('recent');
  const [destinationsTab, setDestinationsTab] = useState<SourceTab>('recent');
  const [categoriesTab, setCategoriesTab] = useState<SourceTab>('recent');
  const [pageSearch, setPageSearch] = useState('');
  const [postSearch, setPostSearch] = useState('');
  const [destinationSearch, setDestinationSearch] = useState('');
  const [destinationScope, setDestinationScope] = useState<DestinationScope>('all');
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [selectedDestinations, setSelectedDestinations] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [customUrl, setCustomUrl] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [openPanel, setOpenPanel] = useState<string | null>('pages');

  const pagesQuery = useQuery({
    queryKey: ['nav-picker-pages'],
    queryFn: () => listPages({ pageSize: 100, sortBy: 'updatedAt', sortDir: 'desc' }),
  });
  const postsQuery = useQuery({
    queryKey: ['nav-picker-posts'],
    queryFn: () => listBlogPosts({ pageSize: 100, sortBy: 'updatedAt', sortDir: 'desc' }),
  });
  const destinationsQuery = useQuery({
    queryKey: ['nav-picker-destinations'],
    queryFn: () =>
      listDestinationPages({ page: 1, pageSize: 100, sortBy: 'updatedAt', sortDir: 'desc' }),
  });
  const categoriesQuery = useQuery({
    queryKey: ['nav-picker-category-pages'],
    queryFn: () =>
      listCategoryPages({ page: 1, pageSize: 100, sortBy: 'updatedAt', sortDir: 'desc' }),
  });
  const destinationsMasterQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
  });

  const pages = pagesQuery.data?.data ?? [];
  const posts = postsQuery.data?.data ?? [];
  const destinations = destinationsQuery.data?.data ?? [];
  const categoryPages = categoriesQuery.data?.data ?? [];

  const destinationIntlById = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const d of destinationsMasterQuery.data?.data ?? []) {
      map.set(d.id, d.isInternational);
    }
    return map;
  }, [destinationsMasterQuery.data]);

  const filterRows = <T extends { id: string; label: string }>(
    rows: T[],
    tab: SourceTab,
    search: string,
    recentLimit = 10,
  ): T[] => {
    let list = rows;
    if (tab === 'search' && search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => r.label.toLowerCase().includes(q));
    } else if (tab === 'recent') {
      list = list.slice(0, recentLimit);
    }
    return list;
  };

  const pageRows = useMemo(
    () =>
      filterRows(
        pages.map((p) => ({ id: p.id, label: p.title, slug: p.slug })),
        pagesTab,
        pageSearch,
      ),
    [pages, pagesTab, pageSearch],
  );

  const postRows = useMemo(
    () =>
      filterRows(
        posts.map((p) => ({ id: p.id, label: p.title, slug: p.slug })),
        postsTab,
        postSearch,
      ),
    [posts, postsTab, postSearch],
  );

  const destinationRows = useMemo(() => {
    let list = filterRows(
      destinations.map((p) => ({ id: p.id, label: p.title, slug: p.slug })),
      destinationsTab,
      destinationSearch,
    );
    if (destinationScope !== 'all') {
      list = list.filter((row) => {
        const page = destinations.find((p) => p.id === row.id);
        if (!page?.destinationId) return false;
        const isInternational = destinationIntlById.get(page.destinationId);
        if (isInternational === undefined) return false;
        return destinationScope === 'international' ? isInternational : !isInternational;
      });
    }
    return list;
  }, [destinations, destinationsTab, destinationSearch, destinationScope, destinationIntlById]);

  const categoryRows = useMemo(
    () =>
      filterRows(
        categoryPages.map((p) => ({ id: p.id, label: p.title, slug: p.slug })),
        categoriesTab,
        categorySearch,
      ),
    [categoryPages, categoriesTab, categorySearch],
  );

  const toggleSet = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const addPages = (): void => {
    const picked = pages.filter((p) => selectedPages.has(p.id));
    onAdd(
      picked.map((p) => ({
        label: p.title,
        href: p.slug === 'home' ? '/' : `/${p.slug}`,
        pageId: p.id,
        itemType: 'page' as const,
      })),
    );
    setSelectedPages(new Set());
  };

  const addPosts = (): void => {
    const picked = posts.filter((p) => selectedPosts.has(p.id));
    onAdd(
      picked.map((p) => ({
        label: p.title,
        href: `/blogs/${p.slug}`,
        pageId: null,
        itemType: 'post' as const,
      })),
    );
    setSelectedPosts(new Set());
  };

  const addDestinations = (): void => {
    const picked = destinations.filter((p) => selectedDestinations.has(p.id));
    onAdd(
      picked
        .map((p) => ({
          label: p.title,
          href: p.primaryCategorySlug
            ? destinationPagePublicPath(p.primaryCategorySlug, p.slug)
            : null,
          pageId: null,
          itemType: 'destination' as const,
        }))
        .filter((item) => item.href !== null),
    );
    setSelectedDestinations(new Set());
  };

  const addCategories = (): void => {
    const picked = categoryPages.filter((p) => selectedCategories.has(p.id));
    onAdd(
      picked.map((p) => ({
        label: p.title,
        href: `/${p.slug}`,
        pageId: null,
        itemType: 'category' as const,
      })),
    );
    setSelectedCategories(new Set());
  };

  const addCustom = (): void => {
    const url = customUrl.trim();
    const label = customLabel.trim() || url;
    if (!url) return;
    const href =
      url.startsWith('http') ||
      url.startsWith('mailto:') ||
      url.startsWith('tel:') ||
      url.startsWith('#')
        ? url
        : url.startsWith('/')
          ? url
          : `/${url}`;
    onAdd([{ label, href, pageId: null, itemType: 'custom' }]);
    setCustomUrl('');
    setCustomLabel('');
  };

  const panel = (id: string, title: string, children: React.ReactNode): React.JSX.Element => (
    <UzCollapsibleSection
      title={title}
      open={openPanel === id}
      onOpenChange={(open) => setOpenPanel(open ? id : null)}
      defaultOpen={false}
      className="border-(--border) bg-(--bg)"
    >
      {children}
    </UzCollapsibleSection>
  );

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-(--fg)">Add menu items</h2>
      {footerMode && (
        <p className="text-xs text-(--fg-faint)">
          Picked links are added to the selected footer section.
        </p>
      )}

      {panel(
        'pages',
        'Pages',
        <>
          <SourceTabs tab={pagesTab} onTab={setPagesTab} />
          {pagesTab === 'search' && (
            <div className="relative mb-2">
              <Search className="absolute top-2.5 left-2 h-3.5 w-3.5 text-(--fg-faint)" />
              <UzInput
                value={pageSearch}
                onChange={(e) => setPageSearch(e.target.value)}
                placeholder="Search pages…"
                className="h-8 pl-8 text-sm"
              />
            </div>
          )}
          <CheckList
            rows={pageRows}
            selected={selectedPages}
            disabled={!canWrite}
            onToggle={(id) => setSelectedPages((s) => toggleSet(s, id))}
            onToggleAll={() =>
              setSelectedPages(
                pageRows.every((r) => selectedPages.has(r.id))
                  ? new Set()
                  : new Set(pageRows.map((r) => r.id)),
              )
            }
          />
          <UzButton
            size="sm"
            className="mt-2 w-full"
            disabled={!canWrite || selectedPages.size === 0}
            onClick={addPages}
          >
            Add to Menu
          </UzButton>
        </>,
      )}

      {panel(
        'posts',
        'Posts',
        <>
          <SourceTabs tab={postsTab} onTab={setPostsTab} />
          {postsTab === 'search' && (
            <div className="relative mb-2">
              <Search className="absolute top-2.5 left-2 h-3.5 w-3.5 text-(--fg-faint)" />
              <UzInput
                value={postSearch}
                onChange={(e) => setPostSearch(e.target.value)}
                placeholder="Search posts…"
                className="h-8 pl-8 text-sm"
              />
            </div>
          )}
          <CheckList
            rows={postRows}
            selected={selectedPosts}
            disabled={!canWrite}
            onToggle={(id) => setSelectedPosts((s) => toggleSet(s, id))}
            onToggleAll={() =>
              setSelectedPosts(
                postRows.every((r) => selectedPosts.has(r.id))
                  ? new Set()
                  : new Set(postRows.map((r) => r.id)),
              )
            }
          />
          <UzButton
            size="sm"
            className="mt-2 w-full"
            disabled={!canWrite || selectedPosts.size === 0}
            onClick={addPosts}
          >
            Add to Menu
          </UzButton>
        </>,
      )}

      {panel(
        'custom',
        'Custom Links',
        <div className="flex flex-col gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-(--fg-faint)">URL</label>
            <UzInput
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="https://… or /path"
              disabled={!canWrite}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-(--fg-faint)">Link Text</label>
            <UzInput
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="Label shown in menu"
              disabled={!canWrite}
              className="h-8 text-sm"
            />
          </div>
          <UzButton size="sm" disabled={!canWrite || !customUrl.trim()} onClick={addCustom}>
            Add to Menu
          </UzButton>
        </div>,
      )}

      {panel(
        'destinations',
        'Destination Pages',
        <>
          <SourceTabs tab={destinationsTab} onTab={setDestinationsTab} />
          <DestinationScopeTabs scope={destinationScope} onScope={setDestinationScope} />
          {destinationsTab === 'search' && (
            <div className="relative mb-2">
              <Search className="absolute top-2.5 left-2 h-3.5 w-3.5 text-(--fg-faint)" />
              <UzInput
                value={destinationSearch}
                onChange={(e) => setDestinationSearch(e.target.value)}
                placeholder="Search destination pages…"
                className="h-8 pl-8 text-sm"
              />
            </div>
          )}
          <CheckList
            rows={destinationRows}
            selected={selectedDestinations}
            disabled={!canWrite}
            onToggle={(id) => setSelectedDestinations((s) => toggleSet(s, id))}
            onToggleAll={() =>
              setSelectedDestinations(
                destinationRows.every((r) => selectedDestinations.has(r.id))
                  ? new Set()
                  : new Set(destinationRows.map((r) => r.id)),
              )
            }
          />
          <UzButton
            size="sm"
            className="mt-2 w-full"
            disabled={!canWrite || selectedDestinations.size === 0}
            onClick={addDestinations}
          >
            Add to Menu
          </UzButton>
        </>,
      )}

      {panel(
        'categories',
        'Category Pages',
        <>
          <SourceTabs tab={categoriesTab} onTab={setCategoriesTab} />
          {categoriesTab === 'search' && (
            <div className="relative mb-2">
              <Search className="absolute top-2.5 left-2 h-3.5 w-3.5 text-(--fg-faint)" />
              <UzInput
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                placeholder="Search category pages…"
                className="h-8 pl-8 text-sm"
              />
            </div>
          )}
          <CheckList
            rows={categoryRows}
            selected={selectedCategories}
            disabled={!canWrite}
            onToggle={(id) => setSelectedCategories((s) => toggleSet(s, id))}
            onToggleAll={() =>
              setSelectedCategories(
                categoryRows.every((r) => selectedCategories.has(r.id))
                  ? new Set()
                  : new Set(categoryRows.map((r) => r.id)),
              )
            }
          />
          <UzButton
            size="sm"
            className="mt-2 w-full"
            disabled={!canWrite || selectedCategories.size === 0}
            onClick={addCategories}
          >
            Add to Menu
          </UzButton>
        </>,
      )}
    </div>
  );
};

/** Build flat draft rows from picker selections (appended at end). */
export const draftsFromPicker = (inputs: MenuItemDraftInput[], startDepth = 0): FlatDraftItem[] =>
  inputs.map((input) => ({
    id: newTempId(),
    label: input.label,
    href: input.href,
    pageId: input.pageId,
    openInNewTab: false,
    isVisible: true,
    depth: startDepth,
    itemType: input.itemType,
    isNew: true,
  }));
